import { z } from "zod";

export const MCP_SERVER_NAME = /^[a-z0-9][a-z0-9-]{0,62}$/;
export const MCP_TOOL_NAME = /^[a-zA-Z0-9_-]{1,64}$/;
export const MAX_MCP_SERVERS = 16;
export const MAX_MCP_TOOLS = 128;
export const MAX_MCP_RESOURCES = 512;

export const mcpConnectModeSchema = z.enum(["eager", "lazy"]);
export type McpConnectMode = z.infer<typeof mcpConnectModeSchema>;

export const mcpToolAnnotationsSchema = z
	.object({
		title: z.string().optional(),
		readOnlyHint: z.boolean().optional(),
		destructiveHint: z.boolean().optional(),
		idempotentHint: z.boolean().optional(),
		openWorldHint: z.boolean().optional(),
	})
	.passthrough();

export type McpToolAnnotations = z.infer<typeof mcpToolAnnotationsSchema>;

export const mcpReviewedPrincipalSchema = z
	.object({ kind: z.enum(["member", "api_key"]), id: z.string().min(1) })
	.strict();

export const mcpToolSnapshotEntrySchema = z
	.object({
		title: z.string().nullable().optional(),
		description: z.string().nullable().optional(),
		inputSchema: z.unknown(),
		annotations: mcpToolAnnotationsSchema.nullable().optional(),
		descriptionDigest: z.string().optional(),
		inputSchemaDigest: z.string().optional(),
		descriptionTruncated: z.boolean().optional(),
		inputSchemaTruncated: z.boolean().optional(),
		reviewedAt: z.string().optional(),
		reviewedBy: mcpReviewedPrincipalSchema.optional(),
	})
	.passthrough();

export type McpToolSnapshotEntry = z.infer<typeof mcpToolSnapshotEntrySchema>;
export const mcpToolSnapshotSchema = z.record(mcpToolSnapshotEntrySchema);
export type McpToolSnapshot = z.infer<typeof mcpToolSnapshotSchema>;

/** Static credential modes supported by remote MCP servers. */
export const mcpStaticAuthSchema = z.discriminatedUnion("type", [
	z.object({ type: z.literal("bearer") }).strict(),
	z
		.object({
			type: z.literal("header"),
			name: z.string().min(1).default("Authorization"),
			format: z.string().default("Bearer {secret}"),
		})
		.strict(),
	z
		.object({
			type: z.literal("cookie"),
			name: z.string().min(1),
			format: z.string().default("{secret}"),
		})
		.strict(),
	z.object({ type: z.literal("query"), name: z.string().min(1) }).strict(),
]);

export type McpStaticAuth = z.infer<typeof mcpStaticAuthSchema>;
export const mcpOAuthAuthSchema = z.object({ type: z.literal("oauth") }).strict();
export type McpOAuthAuth = z.infer<typeof mcpOAuthAuthSchema>;
export type McpAuth = McpStaticAuth | McpOAuthAuth;

const mcpCommonConfigShape = {
	name: z.string().regex(MCP_SERVER_NAME, "Lowercase letters, digits, and dashes only"),
	tools: z.array(z.string()).max(MAX_MCP_TOOLS).default([]),
	toolSnapshot: mcpToolSnapshotSchema.default({}),
	resources: z.boolean().default(false),
	resourceSnapshot: z
		.array(
			z
				.object({
					uri: z.string().min(1).max(4096),
					name: z.string().min(1).max(1024),
					title: z.string().max(1024).nullable().optional(),
					description: z
						.string()
						.max(64 * 1024)
						.nullable()
						.optional(),
					mimeType: z.string().max(255).nullable().optional(),
				})
				.strict(),
		)
		.max(MAX_MCP_RESOURCES)
		.default([]),
	snapshotDigest: z.string().optional(),
	drift: z.enum(["strict", "live"]).default("strict"),
	connect: mcpConnectModeSchema.default("eager"),
	required: z.boolean().default(false),
	callTimeoutMs: z.number().int().min(1_000).max(600_000).default(60_000),
};

export const mcpConnectionEnvReferenceSchema = z
	.string()
	.regex(
		/^\$\{connection\.[a-z0-9][a-z0-9-]{0,62}\.(?:token|url)\}$/,
		"Use one whole ${connection.<name>.token} or ${connection.<name>.url} reference",
	);

export const managedHttpMcpServerConfigSchema = z
	.object({
		...mcpCommonConfigShape,
		url: z.string().url(),
		auth: z.union([mcpStaticAuthSchema, mcpOAuthAuthSchema]),
		secret: z.string().optional(),
	})
	.strict()
	.refine((value) => value.auth.type !== "oauth" || value.secret === undefined, {
		message: "OAuth credentials are obtained with Connect and never appear in forgelab.yml",
		path: ["secret"],
	});

export const referencedHttpMcpServerConfigSchema = z
	.object({
		...mcpCommonConfigShape,
		connection: z.string().regex(MCP_SERVER_NAME),
		path: z.string().default("/mcp"),
	})
	.strict();

const stdioMcpServerBaseShape = {
	...mcpCommonConfigShape,
	args: z.array(z.string().max(4096)).max(128).default([]),
	cwd: z.string().min(1).max(1024).default("."),
	env: z.record(z.string().max(8192)).default({}),
	startupTimeoutMs: z.number().int().min(1_000).max(120_000).default(10_000),
};

function validateStdioConfig(
	value: { cwd: string; env: Record<string, string> },
	ctx: z.RefinementCtx,
): void {
	if (
		value.cwd.startsWith("/") ||
		value.cwd.includes("\\") ||
		/(?:^|\/)\.\.(?:\/|$)/.test(value.cwd)
	) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "cwd must stay inside the deployed bundle",
			path: ["cwd"],
		});
	}
	if (
		!Object.values(value.env).every(
			(envValue) =>
				!envValue.includes("${") || mcpConnectionEnvReferenceSchema.safeParse(envValue).success,
		)
	) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: "Environment values must be literals or whole connection token/URL references",
			path: ["env"],
		});
	}
}

export const commandMcpServerConfigSchema = z
	.object({
		...stdioMcpServerBaseShape,
		command: z.string().trim().min(1).max(1024),
	})
	.strict()
	.superRefine(validateStdioConfig);

export const packageMcpServerConfigSchema = z
	.object({
		...stdioMcpServerBaseShape,
		package: z.string().trim().min(1).max(512),
	})
	.strict()
	.superRefine(validateStdioConfig);

export const mcpServerConfigSchema = z
	.union([
		managedHttpMcpServerConfigSchema,
		referencedHttpMcpServerConfigSchema,
		commandMcpServerConfigSchema,
		packageMcpServerConfigSchema,
	])
	.superRefine((value, ctx) => {
		if (value.connect === "lazy" && value.drift === "live") {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["connect"],
				message: "Lazy MCP connection requires strict drift verification",
			});
		}
	});

export type McpServerConfig = z.infer<typeof mcpServerConfigSchema>;
/** Authoring shape before schema defaults are materialized. */
export type McpServerConfigInput = z.input<typeof mcpServerConfigSchema>;

/** Local stdio server rendered into a runner bootstrap. */
export interface McpStdioServerSpec {
	name: string;
	transport: "stdio";
	command: string;
	args: string[];
	cwd: string;
	env: Record<string, string>;
	startupTimeoutMs: number;
	tools: string[];
	snapshot: McpToolSnapshot;
	resources?: boolean;
	resourceSnapshot?: McpResourceDef[];
	drift: "strict" | "live";
	connect?: McpConnectMode;
	required: boolean;
	callTimeoutMs: number;
	serverDeclarationDigest: string;
	/** Immutable package directory mounted read-only, absent for bundle commands. */
	artifactPath?: string;
	artifactDigest?: string;
}

export interface McpHttpServerSpec {
	name: string;
	transport: "http";
	url: string;
	tools: string[];
	snapshot: McpToolSnapshot;
	resources?: boolean;
	resourceSnapshot?: McpResourceDef[];
	drift: "strict" | "live";
	connect?: McpConnectMode;
	required: boolean;
	callTimeoutMs: number;
	serverDeclarationDigest: string;
	connectionId: string;
	path: string;
	/** Host-rendering failure kept inside the sandbox bootstrap boundary. */
	connectionError?: string;
}

export type McpServerSpec = McpHttpServerSpec | McpStdioServerSpec;

export interface McpToolDef {
	name: string;
	title?: string | null;
	description?: string | null;
	inputSchema: unknown;
	annotations?: McpToolAnnotations | null;
	readonly descriptionDigest?: string;
	readonly inputSchemaDigest?: string;
}

export interface McpResourceDef {
	uri: string;
	name: string;
	title?: string | null;
	description?: string | null;
	mimeType?: string | null;
}

export type McpResultContent =
	| { type: "text"; text: string; truncated?: boolean }
	| { type: "image"; data: string; mimeType: string; truncated?: boolean }
	| { type: "audio"; data: string; mimeType: string; truncated?: boolean }
	| {
			type: "resource";
			uri: string;
			mimeType?: string;
			text?: string;
			blob?: string;
			truncated?: boolean;
	  }
	| {
			type: "resource_link";
			uri: string;
			name: string;
			title?: string;
			description?: string;
			mimeType?: string;
	  }
	| { type: "structured"; value: unknown; truncated?: boolean }
	| { type: "fallback"; label: string; reason: string };

export type McpToolDriftKind =
	| "missing"
	| "schema_changed"
	| "description_changed"
	| "title_changed"
	| "annotations_changed";

export interface McpToolDrift {
	tool: string;
	kinds: McpToolDriftKind[];
	dropped: boolean;
}

export interface McpLastStatus {
	at: string;
	state: "ready" | "error" | "drifted";
	tools?: string[];
	drift?: McpToolDrift[];
	error?: string;
	connect?: McpConnectMode;
	resources?: number;
}

export function mcpToolFullName(server: string, tool: string): string {
	return `mcp__${server}__${tool}`;
}

export function mcpResourceToolFullName(server: string): string {
	return mcpToolFullName(server, "resources");
}

export function mcpToolDefaultNeedsApproval(annotations?: McpToolAnnotations | null): boolean {
	return (
		annotations?.destructiveHint === true ||
		(annotations?.readOnlyHint === false && annotations?.idempotentHint !== true)
	);
}
